<!-- src\components\sections\ServicesSection.vue -->
<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import Spoller from '@/components/ui/SpollerBlock.vue'
import bg from '@/assets/img/services/services_bg.svg'
import cursor from '@/assets/img/services/cursor.png'

const props = defineProps({
	items: {
		type: Array,
		required: true,
		default: () => [],
	},
})

const sortedItems = computed(() => {
	return [...props.items].sort((a, b) => a.id - b.id)
})

const spollerComponent = ref(null)
const spollersItem = ref([])
const spollersTitle = ref([])
const step = ref(0)
const totalSteps = ref(0)
const center = ref(null)
let squares = []

// Обёртка, вызывается после того как DOM отрисовался
async function setupDOM() {
	await nextTick()

	const pixelsConfig = [
		{ name: 'pxTopRight2', selector: '.services__pixel-top-right .services__pxl:nth-child(2)' },
		{ name: 'pxTopLeft2', selector: '.services__pixel-top-left .services__pxl:nth-child(2)' },
		{
			name: 'pxBottomLeft2',
			selector: '.services__pixel-bottom-left .services__pxl:nth-child(2)',
		},
		{
			name: 'pxBottomRight2',
			selector: '.services__pixel-bottom-right .services__pxl:nth-child(2)',
		},
		{
			name: 'pxBottomLeft3',
			selector: '.services__pixel-bottom-left .services__pxl:nth-child(3)',
		},
		{
			name: 'pxBottomRight3',
			selector: '.services__pixel-bottom-right .services__pxl:nth-child(3)',
		},
		{ name: 'pxTopRight3', selector: '.services__pixel-top-right .services__pxl:nth-child(3)' },
	]

	squares = pixelsConfig.map(({ selector }) => {
		const el = document.querySelector(selector)
		if (!el) console.warn(`Pixel not found: ${selector}`)
		return el
	})

	totalSteps.value = spollersItem.value.length
}

// Следим за items — инициализируем DOM после загрузки
watch(
	() => props.items,
	async (newVal) => {
		if (newVal.length > 0) {
			await nextTick()
			await setupDOM()
			if (spollerComponent.value) {
				spollerComponent.value.initSpollers() // Запускаем инициализацию спойлеров
			}
		}
	},
	{ immediate: true },
)

async function nextStep() {
	const isMobile = window.innerWidth < 991.98

	await nextTick()

	// Цикличное переключение
	step.value = (step.value + 1) % totalSteps.value
	console.log(step.value)

	if (isMobile) {
		// Открыть строго один спойлер
		const nextIndex = (step.value + 1) % totalSteps.value
		const nextTitle = spollersTitle.value[nextIndex]

		if (nextTitle && spollerComponent.value) {
			spollerComponent.value.openSpollerStrict(nextTitle)
		}
	} else {
		if (step.value === 0) {
			squares.forEach((el) => el?.classList.remove('visible'))
			spollersItem.value.forEach((el) => el.classList.remove('active'))
		}

		squares[step.value]?.classList.add('visible')
		spollersItem.value.forEach((el, i) => {
			el.classList.toggle('active', i === step.value)
		})
	}

	// Анимация spin для центрального блока
	if (center.value) {
		center.value.classList.remove('spin')
		void center.value.offsetWidth
		center.value.classList.add('spin')
	}
}

async function setActiveStep(index) {
	const isMobile = window.innerWidth < 991.98
	step.value = index

	if (isMobile) {
		const targetTitle = spollersTitle.value[index]
		if (targetTitle && spollerComponent.value) {
			spollerComponent.value.openSpollerStrict(targetTitle)
		}
	} else {
		await nextTick()
		squares.forEach((el) => el?.classList.remove('visible'))
		squares[index]?.classList.add('visible')

		spollersItem.value.forEach((el, i) => {
			el.classList.toggle('active', i === index)
		})
	}

	if (center.value) {
		center.value.classList.remove('spin')
		void center.value.offsetWidth
		center.value.classList.add('spin')
	}
}
</script>

<template>
	<section class="services" id="services" :data-step="step">
		<div
			class="services__bg"
			v-parallax
			:data-prlx-cx="1000"
			:data-prlx-cy="1000"
			:data-prlx-a="50"
			:data-prlx-dxr="true"
			:data-prlx-dyr="true"
		>
			<img :src="bg" alt="" />
		</div>

		<div class="services__container">
			<div class="section-header">
				<h2>Услуги</h2>
			</div>

			<div class="services__wrapper">
				<Spoller :dataSpollers="'991.98,max'" :oneSpoller="true" ref="spollerComponent">
					<div
						class="spollers__item"
						v-for="(item, index) in sortedItems"
						:key="item.id"
						ref="spollersItem"
					>
						<button
							class="spollers__title"
							data-spoller
							ref="spollersTitle"
							@click="setActiveStep(index)"
						>
							{{ item.title }}
						</button>
						<div class="spollers__body">
							<h5>{{ item.description }}</h5>
						</div>
					</div>
				</Spoller>

				<div class="services__block">
					<div class="services__pixels">
						<div class="services__pixel services__pixel-top-left">
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
						</div>
						<div class="services__pixel services__pixel-top-right">
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
						</div>
						<div class="services__pixel services__pixel-bottom-right">
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
						</div>
						<div class="services__pixel services__pixel-bottom-left">
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
						</div>

						<div
							class="services__pixel services__pixel-center"
							ref="center"
							@click="nextStep"
							:aria-expanded="false"
							role="button"
							tabindex="0"
						>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
							<div class="services__pxl"></div>
						</div>
					</div>

					<img :src="cursor" alt="" />
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.services {
	padding: 6.7rem 0;
	position: relative;

	@media (min-width: 991.98px) {
		height: 96rem;
	}

	@media (max-width: 991.98px) {
		padding: 3.8rem 0 3.2rem;
	}

	// .services__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		@media (max-width: 991.98px) {
			display: none;
		}
	}

	// .services__container
	&__container {
		@media (min-width: 991.98px) {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
		}
	}

	// .services__wrapper
	&__wrapper {
		position: relative;
		width: 100%;

		@media (min-width: 991.98px) {
			max-width: 96rem;
			height: 85.3rem;
		}
	}

	// .services__block
	&__block {
		position: absolute;
		top: 25.2%;
		right: 40.6%;

		@media (max-width: 991.98px) {
			top: -14.2rem;
			right: -0.4%;
		}

		& > img {
			position: absolute;
			z-index: 10;
			top: 66%;
			left: 50%;
			transform: translate(-50%, -50%);
			pointer-events: none;
			transition: all 0.5s ease 0s;
			animation: moveCursor 1s linear 0s infinite;

			@media (max-width: 991.98px) {
				width: 3.3rem;
				height: 4.3rem;
			}
		}

		&:active {
			img {
				transform: translate(-50%, -50%) scale(1.1);
			}
		}

		@keyframes moveCursor {
			0% {
				transform: translate(-50%, -50%);
			}

			50% {
				transform: translate(-50%, -60%);
			}

			100% {
				transform: translate(-50%, -50%);
			}
		}
	}

	// .services__pixels
	&__pixels {
	}

	// .services__pixel
	&__pixel {
		position: absolute;
	}

	&__pxl {
		width: 10.7rem;
		height: 10.6rem;
		position: absolute;
		background: #5c6366;

		@media (max-width: 991.98px) {
			width: 4.6rem;
			height: 4.6rem;
		}
	}

	// .services__pixel-top-left
	&__pixel-top-left {
		bottom: 100%;
		right: 100%;
		width: 10.7rem;
		height: 10.6rem;

		@media (max-width: 991.98px) {
			display: none;
		}

		.services__pxl {
			&:nth-child(2) {
				bottom: 100%;
				right: 100%;
				opacity: 0;
			}
		}
	}

	// .services__pixel-top-right
	&__pixel-top-right {
		bottom: 100%;
		left: 100%;
		width: 10.7rem;
		height: 10.6rem;

		@media (max-width: 991.98px) {
			display: none;
		}

		.services__pxl {
			&:nth-child(2) {
				bottom: 100%;
				left: 100%;
				opacity: 0;
			}

			&:nth-child(3) {
				bottom: 0;
				left: 200%;
				opacity: 0;
			}
		}
	}

	// .services__pixel-bottom-right
	&__pixel-bottom-right {
		top: 100%;
		left: 100%;
		width: 10.7rem;
		height: 10.6rem;

		@media (max-width: 991.98px) {
			display: none;
		}

		.services__pxl {
			&:nth-child(2) {
				top: 100%;
				left: 100%;
				opacity: 0;
			}

			&:nth-child(3) {
				top: 200%;
				left: 200%;
				opacity: 0;
			}
		}
	}

	// .services__pixel-bottom-left
	&__pixel-bottom-left {
		top: 100%;
		right: 100%;
		width: 10.7rem;
		height: 10.6rem;

		@media (max-width: 991.98px) {
			display: none;
		}

		.services__pxl {
			&:nth-child(2) {
				top: 100%;
				right: 100%;
				opacity: 0;
			}

			&:nth-child(3) {
				top: 200%;
				right: 200%;
				opacity: 0;
			}
		}
	}

	// .services__pixel-center
	&__pixel-center {
		width: 32rem;
		height: 32rem;
		position: relative;
		z-index: 10;
		cursor: pointer;

		@media (max-width: 991.98px) {
			width: 13.9rem;
			height: 13.7rem;
		}

		.services__pxl {
			background: #f06793;

			&:nth-child(1) {
				top: 50%;
				transform: translateY(-50%);
				left: 0;
			}

			&:nth-child(2) {
				top: 0;
				transform: translateX(-50%);
				left: 50%;
			}

			&:nth-child(3) {
				top: 50%;
				transform: translateY(-50%);
				right: 0;
			}

			&:nth-child(4) {
				bottom: 0;
				transform: translateX(-50%);
				left: 50%;
			}
		}
	}
}
.spollers {
	@media (max-width: 991.98px) {
		margin-top: 5.4rem;
	}

	// .spollers__item
	&__item {
		@media (max-width: 991.98px) {
			&:not(:last-child) {
				margin-bottom: 0.95rem;
			}
		}

		@media (min-width: 991.98px) {
			&:nth-child(1) {
				.spollers__title {
					bottom: 27.2%;
					left: -36.7%;
				}
			}

			&:nth-child(2) {
				.spollers__title {
					top: 39.5%;
					left: -13.1%;
				}
			}

			&:nth-child(3) {
				.spollers__title {
					top: 19.3%;
					left: -20.3%;
				}
			}

			&:nth-child(4) {
				.spollers__title {
					left: 33%;
					top: 13.6%;
				}
			}

			&:nth-child(5) {
				.spollers__title {
					right: 6.2%;
					top: 39.5%;
				}
			}

			&:nth-child(6) {
				.spollers__title {
					bottom: 13.5%;
					left: 47.7%;
				}
			}

			&:nth-child(7) {
				.spollers__title {
					bottom: 25.3%;
					left: 36.8%;
				}
			}
		}
	}

	// .spollers__title
	&__title {
		font-weight: 900;
		font-size: 6.2rem;
		text-transform: uppercase;
		-webkit-text-stroke: 1px #fff;
		text-stroke: 1px #fff;
		color: transparent;
		transition: color 0.3s ease 0s;
		display: flex;
		align-items: center;
		justify-content: space-between;
		cursor: pointer;

		@media (min-width: 991.98px) {
			z-index: 9;
			position: absolute;
		}

		@media (max-width: 991.98px) {
			font-size: 3.4rem;
			width: 100%;

			&::after {
				content: '';
				display: block;
				width: 4.8rem;
				height: 4.8rem;
				background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M16 29L24 21L32 29' stroke='white' stroke-width='1.5'/%3e%3c/svg%3e ");
				background-repeat: no-repeat;
				background-position: center;
				background-size: contain;
				transform: rotate(180deg);
				transition: transform 0.3s ease 0s;
			}
		}

		&._spoller-active {
			color: #fff;

			&::after {
				transform: rotate(0deg);
			}
		}
	}

	// .spollers__body
	&__body {
		@media (min-width: 991.98px) {
			position: absolute;
			top: 20.6%;
			left: -66.6%;
			max-width: 30rem;
			opacity: 0;
		}

		@media (max-width: 991.98px) {
			padding-top: 0.8rem;
			padding-bottom: 1rem;
		}
	}
}

@keyframes rotate-bounce {
	0% {
		transform: rotate(0deg);
	}
	60% {
		transform: rotate(200deg);
	}
	80% {
		transform: rotate(170deg);
	}
	100% {
		transform: rotate(180deg);
	}
}

.services__pixel-center.spin {
	animation: rotate-bounce 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.services__pxl {
	transition:
		opacity 0.3s,
		transform 0.3s;

	&.visible {
		opacity: 1 !important;
		transform: scale(1);
	}
}

@media (min-width: 991.98px) {
	.spollers__item.active {
		.spollers__title {
			color: #fff;
		}

		.spollers__body {
			opacity: 1;
			transition: opacity 0.4s ease 0.2s;
		}
	}
}
</style>
